import {
  DocumentBasedLabResultFragment,
  LabKitFragment,
  LabRequisitionFragment,
} from '@pm/graphql';
import { LabHeroCardProps, TrackingStep } from './LabHeroCard';

export type { LabHeroCardProps, TrackingStep };

const DETECTED = 'Detected';

// The hero card's "ready" state is driven by whichever result points back at
// the kit/requisition via its labKit/labRequisition back-reference, so the
// linked result is sourced from the labWorks array at the call site rather
// than nested under the kit/requisition itself. Shape is narrowed to only the
// fields the hero card actually reads so callers can pass either the full
// fragment or a lighter-weight projection.
export type LinkedLabResult =
  | {
      __typename: 'DetailedLabResult';
      id: string;
      observations: {
        testName: string | null;
        result: string | null;
      }[];
    }
  | { __typename: 'DocumentBasedLabResult'; id: string };

const isDetailedResult = (
  result: LinkedLabResult,
): result is Extract<LinkedLabResult, { __typename: 'DetailedLabResult' }> =>
  result.__typename === 'DetailedLabResult';

const detectedTestNamesFromResult = (result: LinkedLabResult): string[] => {
  if (!isDetailedResult(result)) return [];
  return (result.observations ?? [])
    .filter((o) => o.result === DETECTED)
    .map((o) => o.testName)
    .filter((name): name is string => name != null);
};

const STALE_RESULT_THRESHOLD_DAYS = 30;

const isStale = (createdAt: string) =>
  Date.now() - new Date(createdAt).getTime() >
  STALE_RESULT_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;

// Kit hasn't been handed off to the carrier yet — patient is still holding it
// after outbound delivery. Seed data uses "Pre-Shipment", prod USPS sync matches
// the same string; null/"Unprocessed" cover kits created before any sync ran.
const PRE_TRANSIT_STATUSES = new Set(['Pre-Shipment', 'Unprocessed']);

const isPreTransit = (status: string | null | undefined) =>
  status == null || PRE_TRANSIT_STATUSES.has(status);

// Derives the 1–4 tracking step from the kit's shipment timeline. Callers should
// only invoke this when the kit has no linked result — a linked result means the
// card renders the "ready" variant instead. Step 5 is never returned here: a
// result-linked kit routes to the "ready" branch earlier. Whether step 4 makes it
// out of buildLabKitHeroCardProps depends on the caller — see includeNonActionable.
const trackingStepFromShipments = (
  kit: LabKitFragment,
): TrackingStep | null => {
  const { outboundShipment, inboundShipment } = kit;
  if (!outboundShipment) return null;

  if (!outboundShipment.deliveredOn) return 1;
  if (!inboundShipment) return 2;
  if (inboundShipment.deliveredOn) return 4;
  if (isPreTransit(inboundShipment.status)) return 2;
  return 3;
};

type BuildLabHeroCardOptions = {
  supportLink: string;
  isUsaPatient: boolean;
  // Emit steps the patient cannot act on. The Labs hero section is a to-do
  // list, so it drops them; a status card that simply reports where the kit is
  // wants them.
  includeNonActionable?: boolean;
};

export const buildLabKitHeroCardProps = (
  kit: LabKitFragment,
  linkedResult: LinkedLabResult | null,
  { supportLink, includeNonActionable = false }: BuildLabHeroCardOptions,
): Exclude<LabHeroCardProps, { kind: 'empty' }> | null => {
  const date = new Date(kit.createdAt);

  if (linkedResult) {
    return {
      kind: 'lab-kit',
      date,
      status: {
        type: 'ready',
        resultId: linkedResult.id,
        detectedTestNames: detectedTestNamesFromResult(linkedResult),
      },
    };
  }

  const step = trackingStepFromShipments(kit);
  if (step == null) return null;
  // step 4 = lab received the sample (both shipments delivered). The patient
  // can't act on this, so the Labs hero section drops it into All Lab History
  // until results arrive. Callers that report progress rather than prompt for
  // it opt back in via includeNonActionable.
  if (step === 4 && !includeNonActionable) return null;

  return {
    kind: 'lab-kit',
    date,
    status: {
      type: 'tracking',
      step,
      outboundShipment: kit.outboundShipment,
      inboundShipment: kit.inboundShipment,
      supportLink,
    },
  };
};

// LabActions.tsx sources the patient's requisition PDF from the first attachment,
// falling back to formLink only when no attachment is available. Mirror that here
// so the hero CTA points at the same file — formLink also needs Juno config that
// isn't available in all environments, so attachments are the more reliable source.
const requisitionDownloadUrl = (
  requisition: LabRequisitionFragment,
): string | null =>
  requisition.attachments[0]?.downloadUrl ?? requisition.formLink ?? null;

// A requisition's only hero-card action is downloading the form. Once results
// arrive the requisition is considered "done" from the hero section's point of
// view — the result itself shows up as its own labWorks node, and the
// requisition moves to the All Lab History list below.
export const buildLabRequisitionHeroCardProps = (
  requisition: LabRequisitionFragment,
  linkedResult: LinkedLabResult | null,
  { isUsaPatient }: { isUsaPatient: boolean },
): Exclude<LabHeroCardProps, { kind: 'empty' }> | null => {
  if (linkedResult) return null;

  // CA: feedbackStep is the fulfill_lab_requisition_step — if completed, the
  // req is done and belongs in All Lab History, not the hero section.
  if (!isUsaPatient && requisition.feedbackStep?.completedAt) return null;

  // USA: simple_search used a 30-day threshold instead of workflow step state.
  if (isUsaPatient && isStale(requisition.createdAt)) return null;

  const downloadUrl = requisitionDownloadUrl(requisition);
  if (!downloadUrl) return null;

  const feedbackStep = requisition.feedbackStep;
  const feedbackStepUrl =
    !isUsaPatient && feedbackStep?.id && !feedbackStep.completedAt
      ? `/lab-feedback/${feedbackStep.id}`
      : null;

  return {
    kind: 'lab-requisition',
    date: new Date(requisition.createdAt),
    status: {
      type: 'needs-download',
      requisitionId: requisition.id,
      readByPatientAt: requisition.readByPatientAt,
      downloadUrl,
      feedbackStepUrl,
    },
  };
};

export const buildOrphanDocumentBasedResultHeroCardProps = (
  result: DocumentBasedLabResultFragment,
): Exclude<LabHeroCardProps, { kind: 'empty' }> | null => {
  if (isStale(result.createdAt)) return null;
  return {
    kind: 'lab-result-document',
    date: new Date(result.documentUploadedAt ?? result.createdAt),
    status: {
      type: 'view-document',
      resultId: result.id,
    },
  };
};

const isLabKitFragment = (
  node: LabKitFragment | LabRequisitionFragment,
): node is LabKitFragment => node.__typename === 'LabKit';

// Single dispatch entry point — LabsPage's heroCardPropsFor wrapper is the only
// call site, but exporting this reduces the surface callers need to import.
export const buildHeroCardProps = (
  node: LabKitFragment | LabRequisitionFragment,
  linkedResult: LinkedLabResult | null,
  options: BuildLabHeroCardOptions,
): Exclude<LabHeroCardProps, { kind: 'empty' }> | null =>
  isLabKitFragment(node)
    ? buildLabKitHeroCardProps(node, linkedResult, options)
    : buildLabRequisitionHeroCardProps(node, linkedResult, options);
